import type { CoreContext } from "./context";
import { messageFromError } from "./format";
import type { IdentityFlows } from "./identity-flows";
import type { PayFlows } from "./pay-flows";
import type { createPayTimeline } from "./pay-timeline";
import { CARD_REUSE_CONSENT } from "./ports";
import type { TimerHandle } from "./ports";
import type { Reaction } from "./reactions";
import type { CoreState } from "./state";
import { isSessionFinal } from "./state-machine";
import {
  codeRequestOf,
  isAwaitingHost,
  isMoving,
  isPaying,
  isZeroTotal,
  payTargetOf,
  sessionOf,
} from "./state";

/** How long a finalizing payment is polled before it is told as still
 *  processing. */
export const FINALIZE_TIMEOUT_MS = 180_000;
/** A finalizing payment's session is read at once, then about every second
 *  (a read is cheap, and the tickets usually follow the money within a few)
 *  until it has been finalizing for FINALIZE_FAST_FOR_MS, then every
 *  FINALIZE_POLL_MS until FINALIZE_TIMEOUT_MS. */
export const FINALIZE_FAST_POLL_MS = 1000;
export const FINALIZE_FAST_FOR_MS = 15_000;
export const FINALIZE_POLL_MS = 4000;

/** How long after a read of a finalizing payment's session, answered
 *  `elapsed` ms after it began finalizing, the next one is made. */
export const finalizePollDelay = (elapsed: number) =>
  elapsed < FINALIZE_FAST_FOR_MS ? FINALIZE_FAST_POLL_MS : FINALIZE_POLL_MS;
/** How often a hold past its time is asked about until the engine has
 *  released it (its sweeper runs every minute). */
export const EXPIRY_POLL_MS = 5000;

const TERMINAL_KINDS = [
  "success",
  "expired",
  "refused",
  "processing_timeout",
  "error",
];

/**
 * Everything the checkout does on its own, in the order it does it: each
 * reaction runs again only when what it depends on changes (see
 * reactions.ts). The order matters where two react to the same change —
 * the page hears `paid` before `succeeded`, for one.
 */
export function checkoutReactions(
  ctx: CoreContext,
  identity: IdentityFlows,
  timeline: ReturnType<typeof createPayTimeline>,
  pay: PayFlows,
): Array<Reaction<CoreState>> {
  const { deps, config, set, dispatch, sessionRead, emit, emitOnce, refs } =
    ctx;
  const { buyer, wallet } = deps.engine;
  const { broker } = deps.identity;
  const { clock } = deps;
  const { sessionId, slug, hostIdentity } = config;
  const kind = (s: CoreState) => s.checkout.kind;
  const clearChecking = () =>
    set((s) => ({ busy: s.busy === "checking" ? null : s.busy }));
  const recognised = (s: CoreState) => s.identity.status === "recognised";

  // ---- the session -----------------------------------------------------------

  const loadSession: Reaction<CoreState> = {
    deps: () => [],
    run: () => {
      if (!buyer) {
        dispatch({
          type: "failure",
          message: "Checkout is temporarily unavailable.",
        });
        return;
      }
      const cancellation = { stopped: false };
      const apply = sessionRead();
      void buyer.get(sessionId).then((result) => {
        if (cancellation.stopped) return;
        if (!result.data) {
          dispatch({
            type: "failure",
            message: messageFromError(
              result.error,
              "This checkout link is no longer available.",
            ),
          });
          return;
        }
        if (result.data.organizer.slug !== slug) {
          dispatch({
            type: "failure",
            message: "This checkout link does not match this organizer.",
          });
          return;
        }
        const session = result.data;
        if (!apply({ type: "session_loaded", session })) return;
        emitOnce("session", {
          type: "session",
          expires_at: session.expires_at,
        });
        // The host's attestation can land before this read does: the
        // address it put in place stands.
        const address = session.buyer_email;
        if (address)
          set((s) =>
            s.attested?.email
              ? {}
              : { email: { value: address, chip: true, source: "typed" } },
          );
      });
      return () => {
        cancellation.stopped = true;
      };
    },
  };

  /** Paid: ask until the engine has issued (or refused), then stop. The
   *  checkout has usually asked the engine to settle already (pay-flows.ts);
   *  this is how it hears when that could not, and the webhook did. */
  const pollFinalizing: Reaction<CoreState> = {
    deps: (s) => [kind(s)],
    run: (s) => {
      if (kind(s) !== "finalizing" || !buyer) return;
      let aborted = false;
      // Asked, not read: a cleanup can land during the await.
      const stopped = () => aborted;
      const startedAt = clock.now();
      let timer: TimerHandle | null = null;
      const poll = async (): Promise<void> => {
        if (stopped()) return;
        if (clock.now() - startedAt >= FINALIZE_TIMEOUT_MS) {
          dispatch({ type: "timeout" });
          return;
        }
        const apply = sessionRead();
        // A read that never arrived (offline for a moment) is asked again,
        // not the end of the wait.
        const result = await buyer.get(sessionId).catch(() => null);
        if (stopped()) return;
        if (result?.data) {
          apply({ type: "session_updated", session: result.data });
          if (isSessionFinal(result.data)) return;
        }
        timer = clock.setTimeout(
          () => void poll(),
          finalizePollDelay(clock.now() - startedAt),
        );
      };
      void poll();
      return () => {
        aborted = true;
        if (timer !== null) clock.clearTimeout(timer);
      };
    },
  };

  // The hold's time is up: the engine's sweeper releases it within a minute,
  // and the checkout asks until it has, so the buyer sees the cart expire
  // rather than a timer standing at 0:00. Never while a payment is moving:
  // processing wins over expiry, and the payment's own outcome follows.
  const pollExpiry: Reaction<CoreState> = {
    deps: (s) => [kind(s), sessionOf(s)?.expires_at ?? null, isMoving(s)],
    run: (s) => {
      const expiresAt = sessionOf(s)?.expires_at ?? null;
      if (kind(s) !== "cart" || !buyer || !expiresAt || isMoving(s)) return;
      let stopped = false;
      let timer: unknown = null;
      const ask = async () => {
        const apply = sessionRead();
        const result = await buyer.get(sessionId);
        if (stopped) return;
        if (result.data && result.data.status !== "open") {
          apply({ type: "session_updated", session: result.data });
          return;
        }
        timer = clock.setTimeout(() => void ask(), EXPIRY_POLL_MS);
      };
      timer = clock.setTimeout(
        () => void ask(),
        Math.max(0, new Date(expiresAt).getTime() - clock.now()) + 1000,
      );
      return () => {
        stopped = true;
        clock.clearTimeout(timer);
      };
    },
  };

  const forgetSecret: Reaction<CoreState> = {
    deps: (s) => [kind(s)],
    run: (s) => {
      if (TERMINAL_KINDS.includes(kind(s))) deps.forgetSecret?.();
    },
  };

  // ---- telling the page --------------------------------------------------------

  // Host mode: the money moved and issuing has begun. The page stops
  // holding the buyer in place; the tickets are theirs either way.
  const tellAccepted: Reaction<CoreState> = {
    deps: (s) => [kind(s)],
    run: (s) => {
      if (hostIdentity && kind(s) === "finalizing")
        emitOnce("accepted", { type: "accepted" });
    },
  };

  const tellOutcome: Reaction<CoreState> = {
    deps: (s) => [s.checkout],
    run: (s) => {
      const state = s.checkout;
      const session = sessionOf(s);
      if (state.kind === "success" && session)
        emitOnce("paid", {
          type: "paid",
          order_id: session.order_id ?? null,
          payment_intent_id: refs.paymentIntentId,
          status: "succeeded",
        });
      // Host mode's `succeeded` waits for the status to finish (the timeline);
      // a zero-total session has no status steps, and says it at once
      // (checkout-core.ts).
      if (state.kind === "expired") emitOnce("expired", { type: "expired" });
      if (state.kind === "refused")
        emitOnce("refused", {
          type: "failed",
          // Capacity or the event's checkout limits (LIS-924) refused the
          // purchase after payment; either way nothing was issued and the
          // payment was refunded, so the message names neither.
          message:
            "The purchase could not be completed and the payment was refunded.",
          code: "payment_refused",
        });
      if (state.kind === "error")
        emitOnce(`error:${state.message}`, {
          type: "failed",
          message: state.message,
          code: null,
        });
    },
  };

  // The flag outlives the attempt only until the next idle moment: a
  // sign-in that follows a refused card must not read as a payment.
  const settlePaying: Reaction<CoreState> = {
    deps: (s) => [s.busy, kind(s)],
    run: (s) => {
      if (s.busy === null && kind(s) !== "finalizing")
        set({ payingFlag: false });
    },
  };

  const tellPaying: Reaction<CoreState> = {
    deps: (s) => [isPaying(s)],
    run: (s) => {
      if (isPaying(s)) emit({ type: "paying" });
    },
  };

  // The Pay button tells the payment's story, one step at a time.
  const followPayment: Reaction<CoreState> = {
    deps: (s) => [payTargetOf(s, config)],
    run: (s) => timeline.setTarget(payTargetOf(s, config)),
  };

  // ---- the card entry ----------------------------------------------------------

  // The card entry is open from the start: a platform SetupIntent with no
  // customer yet. The customer is made at the moment of payment.
  // Host mode asks for it alongside the session itself rather than after:
  // the page only opens a checkout for tickets that cost something, and the
  // card fields are what the buyer is waiting to see.
  const openCardEntry: Reaction<CoreState> = {
    deps: (s) => [kind(s), isZeroTotal(s), s.setup],
    run: (s) => {
      const early = hostIdentity && kind(s) === "loading";
      if ((kind(s) !== "cart" && !early) || isZeroTotal(s) || !wallet || s.setup)
        return;
      if (refs.setupAsked) return;
      refs.setupAsked = true;
      void wallet
        .walletSetup(sessionId)
        .then((created) => {
          // The account the payment will be made on, known from page open:
          // a saved card's first payment can then carry a device session
          // for the engine's own confirmation (pay-flows.ts). No connected
          // account is the platform account's.
          refs.paymentAccount ??= {
            publishable_key: created.publishable_key,
            connected_account_id: created.connected_account_id,
          };
          set({ setup: created });
        })
        .catch((error: unknown) =>
          set({
            notice: messageFromError(
              error,
              "Card entry is unavailable right now.",
            ),
          }),
        );
    },
  };

  // A saved card's payment is confirmed by the engine with a device session,
  // begun as soon as a payment could use it (pay-flows.ts): the account is
  // named with the setup, and the saved cards come with the attestation.
  const prepareDeviceSession: Reaction<CoreState> = {
    deps: (s) => [
      kind(s),
      isZeroTotal(s),
      s.setup,
      Boolean(s.attested?.saved_cards.length),
      s.busy,
    ],
    run: (s) => pay.prepareDeviceSession(s),
  };

  // ---- who is buying -------------------------------------------------------------

  // The host speaks for the buyer first: Link in Bio or the Lissen app
  // answering the request, or a token a partner page (or the app opening the
  // hosted page) handed over. It is asked once, straight away, as the
  // checkout starts: minting it is the slow part, and it needs only the
  // session's id, so it is under way alongside the first read of the session
  // rather than after it. The engine is shown it only once that read says
  // the cart is open: attesting a session that has already completed claims
  // its order for the token's buyer, which is the paid-then-verified code's
  // to do (LIS-699), never a page load's. Only if the host has nothing, or
  // the engine refuses it, does the checkout resolve the buyer itself —
  // through its broker, when it has one. (How long a promised token may take
  // is the port's business: see host-tokens.ts.)
  const attestFromHost: Reaction<CoreState> = {
    deps: (s) => [isAwaitingHost(s, config), s.attested, kind(s)],
    run: (s) => {
      if (!isAwaitingHost(s, config) || s.attested || !wallet) return;
      if (!refs.hostTried && (kind(s) === "loading" || kind(s) === "cart")) {
        refs.hostTried = true;
        refs.hostToken = deps.identity
          .checkoutToken({ sessionId, consents: [] })
          .then(
            (answer) => ("token" in answer ? answer.token : null),
            () => null,
          );
      }
      const token = refs.hostToken;
      if (!token || refs.hostChecked || kind(s) !== "cart") return;
      refs.hostChecked = true;
      refs.attesting = true;
      const give = () => set({ hostFailed: true });
      void (async () => {
        try {
          const answer = await token;
          if (!answer) return give();
          // Checking only once there is a token to check: waiting on the
          // host is not the buyer's to watch.
          set({ busy: "checking" });
          const result = await identity.attestWith(answer, {
            refreshInBackground: true,
          });
          if (!result?.attested) give();
        } catch {
          give();
        } finally {
          clearChecking();
          refs.attesting = false;
        }
      })();
    },
  };

  // A browser the identity plane recognises is attested without a word.
  const attestRecognised: Reaction<CoreState> = {
    deps: (s) => [
      isAwaitingHost(s, config),
      recognised(s),
      s.identity.connected,
      s.attested,
      kind(s),
      s.identity.maskedEmail,
      s.busy,
    ],
    run: (s) => {
      if (
        isAwaitingHost(s, config) ||
        !broker ||
        !recognised(s) ||
        !s.identity.connected ||
        !wallet ||
        s.attested ||
        refs.attesting ||
        refs.dismissed ||
        // Never mid-payment: its "checking" would clear the payment's busy
        // and put Pay back in the buyer's hands while the money moves.
        s.busy !== null ||
        kind(s) !== "cart"
      )
        return;
      refs.attesting = true;
      set({ busy: "checking" });
      const masked = s.identity.maskedEmail;
      if (masked)
        set((current) => ({
          email: current.email.value
            ? current.email
            : { value: masked, chip: true, source: "lissen", masked: true },
        }));
      void (async () => {
        try {
          const answer = await deps.identity.checkoutToken({ sessionId });
          if ("token" in answer)
            await identity.attestWith(answer.token, {
              refreshInBackground: hostIdentity,
            });
        } catch {
          // Recognition that cannot become an attestation is not the buyer's
          // problem: the page works exactly as it does for a stranger — and
          // the masked address goes with it, so nothing can be paid against
          // an address nobody typed.
          set((current) => ({
            email: current.email.masked
              ? { value: "", chip: false, source: "typed" }
              : current.email,
          }));
          // With the host's verified address there is no stranger's path to
          // fall back to: the address is proved again, here, with a code.
          if (hostIdentity) set({ hostNeedsCode: true });
        } finally {
          clearChecking();
          refs.attesting = false;
        }
      })();
    },
  };

  // Host mode: the identity plane settled without a session this checkout
  // can attest from (or the attestation failed). The address the page
  // verified is proved once more with a code, in place — a host-mode
  // payment is never taken unattested.
  const identitySettled = (s: CoreState) =>
    s.identity.status === "anonymous" ||
    s.identity.status === "known" ||
    s.identity.status === "failed";
  const hostCode: Reaction<CoreState> = {
    deps: (s) => [
      isAwaitingHost(s, config),
      kind(s),
      s.attested,
      codeRequestOf(s),
      s.hostNeedsCode,
      identitySettled(s),
      s.identity.connected,
      sessionOf(s)?.buyer_email,
    ],
    run: (s) => {
      if (
        !hostIdentity ||
        isAwaitingHost(s, config) ||
        kind(s) !== "cart" ||
        s.attested ||
        refs.attesting ||
        codeRequestOf(s) ||
        refs.hostCodeAsked ||
        !(s.hostNeedsCode || identitySettled(s))
      )
        return;
      refs.hostCodeAsked = true;
      const address = sessionOf(s)?.buyer_email ?? null;
      // No broker (the host answers, and had nothing) or none connected: no
      // code can be asked here, so the page is asked to verify again.
      if (!broker || !s.identity.connected || !address) {
        set({
          notice:
            "We couldn't confirm it's you. Go back and enter your email again.",
        });
        return;
      }
      set({ busy: "checking" });
      void identity
        .startCode(s, { email: address }, { kind: "wallet" })
        .finally(clearChecking);
    },
  };

  // An address that arrived on the session (the app's signed-in fan, a
  // partner's prefill) is asked about once, exactly as a typed one is: a
  // Lissen buyer's gets the code in place, Link-style, without retyping.
  const askAboutSessionAddress: Reaction<CoreState> = {
    deps: (s) => [
      isAwaitingHost(s, config),
      sessionOf(s)?.buyer_email,
      isZeroTotal(s),
      s.attested,
      recognised(s),
      codeRequestOf(s),
      s.identity.status !== "loading",
      kind(s),
    ],
    run: (s) => {
      const address = sessionOf(s)?.buyer_email ?? null;
      if (
        hostIdentity ||
        isAwaitingHost(s, config) ||
        !broker ||
        !address ||
        isZeroTotal(s) ||
        !config.identityAvailable ||
        !wallet ||
        s.attested ||
        refs.attesting ||
        recognised(s) ||
        codeRequestOf(s) ||
        s.identity.status === "loading" ||
        kind(s) !== "cart" ||
        refs.enrolmentFor === address
      )
        return;
      refs.enrolmentFor = address;
      void wallet
        .enrolment(sessionId, { email: address })
        .then(({ enrolled }) =>
          enrolled
            ? identity.startCode(s, { email: address }, { kind: "wallet" })
            : null,
        )
        // Not knowing is the same as no: the guest path is unchanged.
        .catch(() => null);
    },
  };

  // After a remembered guest's payment: the code, on the success screen.
  const rememberAfterPaying: Reaction<CoreState> = {
    deps: (s) => [kind(s), s.remember, s.attested, s.identity.connected],
    run: (s) => {
      if (
        kind(s) !== "success" ||
        hostIdentity ||
        !s.remember ||
        s.attested ||
        refs.askedToRemember ||
        !s.identity.connected
      )
        return;
      refs.askedToRemember = true;
      void identity.startCode(
        s,
        {
          email: s.email.value,
          consents: isZeroTotal(s) ? [] : [CARD_REUSE_CONSENT],
        },
        { kind: "remember" },
      );
    },
  };

  return [
    loadSession,
    pollFinalizing,
    pollExpiry,
    forgetSecret,
    tellAccepted,
    tellOutcome,
    settlePaying,
    tellPaying,
    followPayment,
    openCardEntry,
    prepareDeviceSession,
    attestFromHost,
    attestRecognised,
    hostCode,
    askAboutSessionAddress,
    rememberAfterPaying,
  ];
}
